// ============================================
// Team Leader Dashboard Page
// Rich IPD + CSAT per member, Chart.js charts, escalation monitoring
// NEW: Survey Distribution card, CSAT Target Calculator, Animated Team Score Cards
// ============================================

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, RadialLinearScale,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, RadialLinearScale,
  Title, Tooltip, Legend, Filler,
);

// ── Animated counter ──
function useAnimatedCounter(target: number, duration = 1000, decimals = 0) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();
  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = target * eased;
      setValue(decimals > 0 ? Number(current.toFixed(decimals)) : Math.round(current));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, decimals]);
  return value;
}

function AnimatedStat({ icon, label, value, suffix, color, delay, decimals = 0 }: {
  icon: string; label: string; value: number; suffix?: string; color: string; delay: number; decimals?: number;
}) {
  const displayed = useAnimatedCounter(value, 1000, decimals);
  return (
    <div className="glass-card stat-card animate-float-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <span className="stat-card__label" style={{ margin: 0 }}>{label}</span>
        <span style={{ fontSize: '22px', filter: `drop-shadow(0 0 8px ${color})` }}>{icon}</span>
      </div>
      <div className="stat-card__value" style={{ color, textShadow: `0 0 24px ${color}55` }}>
        {decimals > 0 ? displayed.toFixed(decimals) : displayed}{suffix || ''}
      </div>
    </div>
  );
}

const PALETTE = ['#DC143C', '#FF4060', '#FF6B8A', '#4D96FF', '#00D46A', '#FFB300', '#A855F7', '#14B8A6'];
const tooltipStyle = {
  backgroundColor: 'rgba(0,0,0,0.85)',
  titleColor: '#F0F0F8', bodyColor: '#A0A0B8',
  borderColor: 'rgba(220,20,60,0.4)', borderWidth: 1,
  cornerRadius: 8, padding: 12,
  titleFont: { weight: '700' as const, size: 13 }, bodyFont: { size: 12 },
};

// ── Rating color helper ──
const RATING_COLORS: Record<number, { bg: string; border: string; label: string; gradient: string }> = {
  5: { bg: 'rgba(0, 212, 106, 0.15)', border: '#00D46A', label: '⭐⭐⭐⭐⭐', gradient: 'linear-gradient(90deg, #00D46A, #00FF85)' },
  4: { bg: 'rgba(128, 224, 96, 0.12)', border: '#80E060', label: '⭐⭐⭐⭐', gradient: 'linear-gradient(90deg, #80E060, #A8F080)' },
  3: { bg: 'rgba(255, 179, 0, 0.12)', border: '#FFB300', label: '⭐⭐⭐', gradient: 'linear-gradient(90deg, #FFB300, #FFD040)' },
  2: { bg: 'rgba(255, 104, 48, 0.12)', border: '#FF6830', label: '⭐⭐', gradient: 'linear-gradient(90deg, #FF6830, #FF9040)' },
  1: { bg: 'rgba(255, 61, 61, 0.12)', border: '#FF3D3D', label: '⭐', gradient: 'linear-gradient(90deg, #FF3D3D, #FF6060)' },
};

export default function TeamLeaderDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [members, setMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [scoreModal, setScoreModal] = useState<any>(null);
  
  // Scroll to top when modal opens
  useEffect(() => {
    if (scoreModal) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [scoreModal]);

  const [commForm, setCommForm] = useState({
    monthKey: new Date().toISOString().slice(0, 7),
    startingBacklog: '', casesAssigned: '', casesClosed: '',
    n5: '', n4: '', n3: '', n2: '', n1: '',
  });
  const [escalations, setEscalations] = useState<any[]>([]);
  const [escLoading, setEscLoading] = useState(false);
  const [escLoaded, setEscLoaded] = useState(false);
  const [expandedEsc, setExpandedEsc] = useState<string | null>(null);
  const [escThread, setEscThread] = useState<any>(null);

  // ── CSAT Target Calculator state ──
  const [csatTarget, setCsatTarget] = useState('4.70');

  // ── Team Score cards state ──
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [transitionPhase, setTransitionPhase] = useState<'idle' | 'shrinking' | 'expanding'>('idle');
  const [outgoingId, setOutgoingId] = useState<string | null>(null);
  const [incomingId, setIncomingId] = useState<string | null>(null);

  // ── Data loaders (stable callbacks) ──
  const lastLoadRef = useRef<number>(0);

  const loadMembers = useCallback(async () => {
    const nowStamp = Date.now();
    if (nowStamp - lastLoadRef.current < 30000) return; // 30s freshness guard

    setMembersLoading(true);
    setTeamError(null);
    try {
      const data = await api.get<any>('/team/members/scores');
      setMembers(data.members || []);
    } catch (err: any) {
      setTeamError(err.message || 'Failed to load team data');
    }
    lastLoadRef.current = Date.now();
    setMembersLoading(false);
  }, []);

  const loadEscalations = useCallback(async () => {
    setEscLoading(true);
    try {
      const data = await api.get<any>('/escalations/monitor');
      setEscalations(data.escalations || []);
      setEscLoaded(true);
    } catch { }
    setEscLoading(false);
  }, []);

  // ── Tab-aware data loading via useEffect ──
  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'team-score') {
      loadMembers();
    }
    if (activeTab === 'monitor-escalations') {
      loadEscalations();
    }
  }, [activeTab, loadMembers, loadEscalations]);

  const loadEscThread = async (id: string) => {
    try {
      const data = await api.get<any>(`/escalations/${id}`);
      setEscThread(data);
      setExpandedEsc(id);
    } catch { }
  };

  const viewMemberScore = async (member: any) => {
    try {
      const data = await api.get<any>(`/team/score/${member.userId}`);
      setScoreModal({ ...member, ...data });
      // Reset form when opening modal
      setCommForm({
        monthKey: new Date().toISOString().slice(0, 7),
        startingBacklog: '', casesAssigned: '', casesClosed: '',
        n5: '', n4: '', n3: '', n2: '', n1: '',
      });
    } catch { }
  };

  const sendCommunication = async () => {
    if (!scoreModal) return;
    try {
      // Convert empty strings to 0 for the API
      const payload = {
        monthKey: commForm.monthKey,
        startingBacklog: parseInt(commForm.startingBacklog as any) || 0,
        casesAssigned: parseInt(commForm.casesAssigned as any) || 0,
        casesClosed: parseInt(commForm.casesClosed as any) || 0,
        n5: parseInt(commForm.n5 as any) || 0,
        n4: parseInt(commForm.n4 as any) || 0,
        n3: parseInt(commForm.n3 as any) || 0,
        n2: parseInt(commForm.n2 as any) || 0,
        n1: parseInt(commForm.n1 as any) || 0,
      };
      await api.post(`/team/communicate/${scoreModal.userId}`, payload);
      setScoreModal(null);
      loadMembers();
    } catch { }
  };

  // Aggregates for dashboard
  const scoreMembers = members.filter(m => m.role !== 'sme');
  const totalMembers = scoreMembers.length;
  const avgCsat = totalMembers > 0 ? parseFloat((scoreMembers.reduce((s, m) => s + (m.csat?.csatScore || m.csatScore || 0), 0) / totalMembers).toFixed(1)) : 0;
  const totalAssigned = scoreMembers.reduce((s, m) => s + (m.ipd?.casesAssigned || 0), 0);
  const totalClosed = scoreMembers.reduce((s, m) => s + (m.ipd?.casesClosed || 0), 0);
  const totalTransferred = scoreMembers.reduce((s, m) => s + (m.ipd?.casesTransferred || 0), 0);
  const totalNetAssigned = scoreMembers.reduce((s, m) => s + (m.ipd?.netAssigned || 0), 0);
  const totalActiveCases = scoreMembers.reduce((s, m) => s + (m.ipd?.currentBacklog || 0), 0);
  const closureRate = totalAssigned > 0 ? Math.round((totalClosed / totalAssigned) * 100) : 0;
  const avgCsatRating = totalMembers > 0 ? parseFloat((scoreMembers.reduce((s, m) => s + (m.csat?.csatAvg || 0), 0) / totalMembers).toFixed(2)) : 0;

  // ── Team survey aggregation (rating breakdown across team) ──
  const teamSurveyBreakdown = useMemo(() => {
    const b = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as Record<number, number>;
    for (const m of scoreMembers) {
      b[1] += m.csat?.n1 || 0;
      b[2] += m.csat?.n2 || 0;
      b[3] += m.csat?.n3 || 0;
      b[4] += m.csat?.n4 || 0;
      b[5] += m.csat?.n5 || 0;
    }
    return b;
  }, [scoreMembers]);

  const totalSurveys = teamSurveyBreakdown[1] + teamSurveyBreakdown[2] + teamSurveyBreakdown[3] + teamSurveyBreakdown[4] + teamSurveyBreakdown[5];

  // ── CSAT Target Calculator logic ──
  const csatTargetCalc = useMemo(() => {
    const target = parseFloat(csatTarget);
    if (isNaN(target) || target <= 0 || target > 5) return null;

    const currentN5 = teamSurveyBreakdown[5];
    const currentN4 = teamSurveyBreakdown[4];
    const currentN3 = teamSurveyBreakdown[3];
    const currentN2 = teamSurveyBreakdown[2];
    const currentN1 = teamSurveyBreakdown[1];
    const currentTotal = totalSurveys;
    const currentWeighted = 5 * currentN5 + 4 * currentN4 + 3 * currentN3 + 2 * currentN2 + 1 * currentN1;
    const currentAvg = currentTotal > 0 ? currentWeighted / currentTotal : 0;

    if (currentAvg >= target) {
      return { needed: 0, currentAvg, targetMet: true };
    }

    // Solve: (currentWeighted + 5 * x) / (currentTotal + x) >= target
    // currentWeighted + 5x >= target * (currentTotal + x)
    // 5x - target*x >= target*currentTotal - currentWeighted
    // x(5 - target) >= target*currentTotal - currentWeighted
    // x >= (target*currentTotal - currentWeighted) / (5 - target)
    const needed = Math.ceil((target * currentTotal - currentWeighted) / (5 - target));
    return { needed: Math.max(0, needed), currentAvg, targetMet: false };
  }, [csatTarget, teamSurveyBreakdown, totalSurveys]);

  // ── Top achiever: highest CSAT avg, tie-break by most closed ──
  const topAchiever = useMemo(() => {
    if (scoreMembers.length === 0) return null;
    return [...scoreMembers].sort((a, b) => {
      const diff = (b.csat?.csatAvg || 0) - (a.csat?.csatAvg || 0);
      if (diff !== 0) return diff;
      return (b.ipd?.casesClosed || 0) - (a.ipd?.casesClosed || 0);
    })[0];
  }, [scoreMembers]);

  // When entering team-score tab, auto-select top achiever
  useEffect(() => {
    if (activeTab === 'team-score' && topAchiever && !selectedMemberId) {
      setSelectedMemberId(topAchiever.userId);
    }
  }, [activeTab, topAchiever, selectedMemberId]);

  // Chart data
  const csatBarData = {
    labels: scoreMembers.map(m => m.userName.split(' ')[0]),
    datasets: [{
      label: 'CSAT %',
      data: scoreMembers.map(m => m.csat?.csatScore || m.csatScore || 0),
      backgroundColor: scoreMembers.map((m, i) => `${PALETTE[i % PALETTE.length]}CC`),
      borderColor: scoreMembers.map((m, i) => PALETTE[i % PALETTE.length]),
      borderWidth: 1, borderRadius: 6, borderSkipped: false as const,
    }],
  };

  const ipdBarData = {
    labels: scoreMembers.map(m => m.userName.split(' ')[0]),
    datasets: [
      {
        label: 'Net Assigned',
        data: scoreMembers.map(m => m.ipd?.netAssigned || 0),
        backgroundColor: '#A855F7CC',
        borderColor: '#A855F7',
        borderWidth: 1, borderRadius: 4,
      },
      {
        label: 'Closed',
        data: scoreMembers.map(m => m.ipd?.casesClosed || 0),
        backgroundColor: '#00D46ACC',
        borderColor: '#00D46A',
        borderWidth: 1, borderRadius: 4,
      },
      {
        label: 'Transferred',
        data: scoreMembers.map(m => m.ipd?.casesTransferred || 0),
        backgroundColor: '#FFB300CC',
        borderColor: '#FFB300',
        borderWidth: 1, borderRadius: 4,
      }
    ],
  };

  const ipdDoughnutData = {
    labels: ['Assigned', 'Closed', 'Transferred'],
    datasets: [{
      data: [
        totalAssigned,
        totalClosed,
        totalTransferred,
      ],
      backgroundColor: ['#4D96FFCC', '#00D46ACC', '#FFB300CC'],
      borderColor: 'transparent', hoverOffset: 10,
    }],
  };

  // ── Survey Distribution Doughnut Data ──
  const surveyDoughnutData = useMemo(() => ({
    labels: ['★5', '★4', '★3', '★2', '★1'],
    datasets: [{
      data: [teamSurveyBreakdown[5], teamSurveyBreakdown[4], teamSurveyBreakdown[3], teamSurveyBreakdown[2], teamSurveyBreakdown[1]],
      backgroundColor: ['#00D46ACC', '#80E060CC', '#FFB300CC', '#FF6830CC', '#FF3D3DCC'],
      borderColor: ['#00D46A', '#80E060', '#FFB300', '#FF6830', '#FF3D3D'],
      borderWidth: 1,
      hoverOffset: 8,
    }],
  }), [teamSurveyBreakdown]);

  // ── CSAT Target gauge chart data ──
  const targetGaugeData = useMemo(() => {
    if (!csatTargetCalc) return null;
    const { needed, targetMet } = csatTargetCalc;
    if (targetMet) {
      return {
        labels: ['Current 5★', 'Goal Met!'],
        datasets: [{
          data: [teamSurveyBreakdown[5], 0],
          backgroundColor: ['#00D46ACC', 'rgba(255,255,255,0.05)'],
          borderColor: ['#00D46A', 'transparent'],
          borderWidth: 1,
          hoverOffset: 6,
        }],
      };
    }
    return {
      labels: ['Current 5★', 'Needed 5★'],
      datasets: [{
        data: [teamSurveyBreakdown[5], needed],
        backgroundColor: ['#00D46ACC', 'rgba(168, 85, 247, 0.35)'],
        borderColor: ['#00D46A', '#A855F7'],
        borderWidth: 1,
        hoverOffset: 6,
      }],
    };
  }, [csatTargetCalc, teamSurveyBreakdown]);

  const renderDashboard = () => (
    <>
      {membersLoading ? (
        <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
      ) : (
        <>
          <div className="grid-stats" style={{ marginBottom: 'var(--space-4)' }}>
            <AnimatedStat icon="📥" label="Assigned (Team)" value={totalAssigned} color="#4D96FF" delay={0} />
            <AnimatedStat icon="✅" label="Closed (Team)" value={totalClosed} color="#00D46A" delay={80} />
            <AnimatedStat icon="↗️" label="Transferred" value={totalTransferred} color="#FFB300" delay={160} />
            <AnimatedStat icon="📂" label="Active Cases" value={totalActiveCases} color="#DC143C" delay={240} />
          </div>
          <div className="grid-stats" style={{ marginBottom: 'var(--space-6)', gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <AnimatedStat icon="📊" label="Net Assignment" value={totalNetAssigned} color="#A855F7" delay={320} />
            <AnimatedStat icon="📈" label="Closure Rate" value={closureRate} suffix="%" color="#14B8A6" delay={400} />
            <AnimatedStat icon="⭐" label="Team CSAT %" value={Math.round(avgCsat)} suffix="%" color="#FFD700" delay={480} />
            <AnimatedStat icon="⭐" label="CSAT Avg" value={avgCsatRating} decimals={2} color="#FF6B8A" delay={520} />
            <AnimatedStat icon="👥" label="Team Members" value={totalMembers} color="#4D96FF" delay={560} />
          </div>

          {/* ── NEW: Survey Distribution + CSAT Target Calculator ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
            {/* Survey Distribution Card */}
            <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '580ms', animationFillMode: 'both' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: '20px' }}>📋</span> Team Survey Distribution
              </h3>
              <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center' }}>
                {/* Left: Rating bars */}
                <div style={{ flex: 1 }}>
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = teamSurveyBreakdown[rating];
                    const pct = totalSurveys > 0 ? (count / totalSurveys) * 100 : 0;
                    const rc = RATING_COLORS[rating];
                    return (
                      <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: '8px' }}>
                        <span style={{ width: 32, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', fontWeight: 700, flexShrink: 0, textAlign: 'center' }}>★{rating}</span>
                        <div style={{ flex: 1, height: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 'var(--radius-pill)', overflow: 'hidden', position: 'relative' }}>
                          <div style={{
                            height: '100%', width: `${pct}%`, background: rc.gradient,
                            borderRadius: 'var(--radius-pill)',
                            transition: 'width 1s cubic-bezier(0.22, 1, 0.36, 1)',
                            boxShadow: `0 0 8px ${rc.border}40`,
                          }} />
                        </div>
                        <span style={{
                          width: 42, textAlign: 'right', fontSize: 'var(--font-size-sm)',
                          fontWeight: 800, color: rc.border, fontFamily: 'var(--font-mono)',
                        }}>{count}</span>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textAlign: 'center' }}>
                    Total Surveys: <strong style={{ color: 'var(--text-primary)' }}>{totalSurveys}</strong>
                  </div>
                </div>
                {/* Right: Mini doughnut */}
                <div style={{ width: 130, height: 130, flexShrink: 0 }}>
                  <Doughnut data={surveyDoughnutData} options={{
                    responsive: true, maintainAspectRatio: true, cutout: '60%',
                    plugins: {
                      legend: { display: false },
                      tooltip: tooltipStyle as any,
                    },
                    animation: { animateRotate: true, animateScale: true, duration: 1600, easing: 'easeOutQuart' },
                  }} />
                </div>
              </div>
            </div>

            {/* CSAT Target Calculator Card */}
            <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '660ms', animationFillMode: 'both' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: '20px' }}>🎯</span> CSAT Target Calculator
              </h3>
              <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center' }}>
                {/* Left: Input + result */}
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <label className="form-label" style={{ marginBottom: 'var(--space-2)', display: 'block' }}>Target CSAT Average</label>
                    <input
                      className="glass-input"
                      type="text"
                      inputMode="decimal"
                      placeholder="e.g. 4.70"
                      value={csatTarget}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setCsatTarget(val);
                      }}
                      style={{ maxWidth: 160, textAlign: 'center', fontSize: 'var(--font-size-lg)', fontWeight: 800, letterSpacing: '0.02em' }}
                    />
                  </div>
                  {csatTargetCalc && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
                        Current Avg: <strong style={{ color: 'var(--text-primary)' }}>{csatTargetCalc.currentAvg.toFixed(2)}</strong>
                      </div>
                      {csatTargetCalc.targetMet ? (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)',
                          background: 'rgba(0, 212, 106, 0.12)', border: '1px solid rgba(0, 212, 106, 0.35)',
                          borderRadius: 'var(--radius-md)', padding: '8px 14px', marginTop: 'var(--space-2)',
                        }}>
                          <span style={{ fontSize: '18px' }}>✅</span>
                          <span style={{ fontWeight: 700, color: '#00D46A', fontSize: 'var(--font-size-sm)' }}>Target Already Met!</span>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.35)',
                          borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', marginTop: 'var(--space-2)',
                        }}>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 4 }}>
                            5★ surveys needed:
                          </div>
                          <div style={{
                            fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#A855F7',
                            textShadow: '0 0 20px rgba(168, 85, 247, 0.5)',
                            letterSpacing: '-0.02em',
                          }}>
                            {csatTargetCalc.needed}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Right: Animated gauge chart */}
                <div style={{ width: 150, height: 150, flexShrink: 0 }}>
                  {targetGaugeData && (
                    <Doughnut data={targetGaugeData} options={{
                      responsive: true, maintainAspectRatio: true, cutout: '55%',
                      plugins: {
                        legend: { display: false },
                        tooltip: tooltipStyle as any,
                      },
                      animation: { animateRotate: true, animateScale: true, duration: 1800, easing: 'easeOutQuart' },
                    }} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
            <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '700ms', animationFillMode: 'both' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Team CSAT Comparison</h3>
              <div style={{ position: 'relative', maxHeight: '280px' }}>
                <Bar data={csatBarData} options={{
                  responsive: true, maintainAspectRatio: true,
                  plugins: { legend: { display: false }, tooltip: tooltipStyle as any },
                  scales: {
                    x: { grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } }, border: { color: 'rgba(139,0,0,0.2)' } },
                    y: { beginAtZero: true, max: 100, grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' }, callback: (v: any) => `${v}%` }, border: { color: 'rgba(139,0,0,0.2)' } },
                  },
                  animation: { duration: 1400, easing: 'easeOutQuart' },
                }} />
              </div>
            </div>

            <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '800ms', animationFillMode: 'both' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>IPD Output Comparison</h3>
              <div style={{ position: 'relative', maxHeight: '280px' }}>
                <Bar data={ipdBarData} options={{
                  responsive: true, maintainAspectRatio: true,
                  plugins: { legend: { position: 'top', labels: { color: '#A0A0B8', font: { size: 11, family: 'Inter', weight: 'bold' }, usePointStyle: true, boxWidth: 10 } }, tooltip: tooltipStyle as any },
                  scales: {
                    x: { grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } }, border: { color: 'rgba(139,0,0,0.2)' } },
                    y: { beginAtZero: true, grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } }, border: { color: 'rgba(139,0,0,0.2)' } },
                  },
                  animation: { duration: 1400, easing: 'easeOutQuart' },
                }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--space-5)' }}>
            <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '900ms', animationFillMode: 'both', maxWidth: '600px', margin: '0 auto', width: '100%' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)', textAlign: 'center' }}>Overall IPD Summary</h3>
              <div style={{ position: 'relative', maxHeight: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Doughnut data={ipdDoughnutData} options={{
                  responsive: true, maintainAspectRatio: true, cutout: '65%',
                  plugins: {
                    legend: { position: 'bottom', labels: { color: '#A0A0B8', padding: 16, font: { size: 11, family: 'Inter', weight: 'bold' }, usePointStyle: true, pointStyleWidth: 10 } },
                    tooltip: tooltipStyle as any,
                  },
                  animation: { animateRotate: true, animateScale: true, duration: 1600, easing: 'easeOutQuart' },
                }} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

  // ── Animated Team Score Cards ──
  const selectedMember = scoreMembers.find(m => m.userId === selectedMemberId);
  const otherMembers = scoreMembers.filter(m => m.userId !== selectedMemberId);

  const handleSelectMember = (memberId: string) => {
    if (memberId === selectedMemberId || transitionPhase !== 'idle') return;

    // Phase 1: SHRINK the current featured card, highlight the clicked one
    setOutgoingId(selectedMemberId);
    setIncomingId(memberId);
    setTransitionPhase('shrinking');

    // Phase 2: After shrink completes, swap data and EXPAND new card
    setTimeout(() => {
      setSelectedMemberId(memberId);
      setTransitionPhase('expanding');

      // Phase 3: After expand completes, return to idle
      setTimeout(() => {
        setTransitionPhase('idle');
        setOutgoingId(null);
        setIncomingId(null);
      }, 500);
    }, 420);
  };

  const renderMemberMetrics = (m: any, isFeatured: boolean) => {
    const csatPct = m.csat?.csatScore || m.csatScore || 0;
    const csatStatusClass = csatPct >= 80 ? 'success' : csatPct >= 50 ? 'in_progress' : 'critical';
    const isTopAchiever = topAchiever && m.userId === topAchiever.userId;

    if (!isFeatured) {
      // Small card view
      return (
        <div
          className="glass-card glass-card--hover tl-member-card tl-member-card--small"
          onClick={() => handleSelectMember(m.userId)}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div className="glass-avatar glass-avatar--sm" style={{ width: 30, height: 30, fontSize: '11px' }}>
                {m.userName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', lineHeight: 1.2 }}>{m.userName}</div>
                {isTopAchiever && (
                  <span style={{ fontSize: '10px', color: '#FFD700', fontWeight: 700 }}>🏆 Top Achiever</span>
                )}
              </div>
            </div>
            <span className={`glass-badge glass-badge--${csatStatusClass}`} style={{ fontSize: '10px' }}>
              {csatPct}%
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-1)', marginTop: 'var(--space-1)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Closed</div>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 800, color: '#00D46A' }}>{m.ipd?.casesClosed ?? 0}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Active</div>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 800, color: '#4D96FF' }}>{m.ipd?.currentBacklog ?? 0}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '9px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Avg</div>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 800, color: '#FF6B8A' }}>{m.csat?.csatAvg ?? 0}</div>
            </div>
          </div>
        </div>
      );
    }

    // Large featured card
    return (
      <div className="glass-card tl-member-card tl-member-card--featured" style={{ position: 'relative' }}>
        {/* Top glow bar */}
        <div style={{
          position: 'absolute', top: 0, left: '5%', right: '5%', height: 2,
          background: isTopAchiever
            ? 'linear-gradient(90deg, transparent, #FFD70080, #FFD700, #FFD70080, transparent)'
            : 'linear-gradient(90deg, transparent, rgba(220, 20, 60, 0.5), rgba(255, 32, 69, 0.8), rgba(220, 20, 60, 0.5), transparent)',
          borderRadius: 'var(--radius-pill)',
        }} />
        <div style={{ padding: 'var(--space-6)' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div className="glass-avatar glass-avatar--xl" style={{
                boxShadow: isTopAchiever ? '0 0 20px rgba(255, 215, 0, 0.5)' : '0 0 16px rgba(220, 20, 60, 0.4)',
                border: isTopAchiever ? '2px solid rgba(255, 215, 0, 0.6)' : '2px solid rgba(220, 20, 60, 0.5)',
              }}>
                {m.userName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 800, letterSpacing: '-0.02em' }}>{m.userName}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 2 }}>
                  <span className="glass-badge glass-badge--role" style={{ fontSize: '10px' }}>
                    {m.role?.replace(/_/g, ' ')}
                  </span>
                  {isTopAchiever && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: 'rgba(255, 215, 0, 0.12)', border: '1px solid rgba(255, 215, 0, 0.35)',
                      borderRadius: 'var(--radius-pill)', padding: '2px 10px',
                      fontSize: '10px', fontWeight: 800, color: '#FFD700',
                    }}>
                      🏆 Top Achiever
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button className="glass-btn glass-btn--ghost glass-btn--sm" onClick={(e) => { e.stopPropagation(); viewMemberScore(m); }}>
              View & Correct
            </button>
          </div>

          {/* Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Backlog</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#A855F7', textShadow: '0 0 16px rgba(168, 85, 247, 0.4)' }}>{m.ipd?.startingBacklog ?? 0}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Assigned</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#4D96FF', textShadow: '0 0 16px rgba(77, 150, 255, 0.4)' }}>{m.ipd?.casesAssigned ?? 0}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Closed</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#00D46A', textShadow: '0 0 16px rgba(0, 212, 106, 0.4)' }}>{m.ipd?.casesClosed ?? 0}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Transferred</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#FFB300', textShadow: '0 0 16px rgba(255, 179, 0, 0.4)' }}>{m.ipd?.casesTransferred ?? 0}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Net Assigned</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#A855F7', textShadow: '0 0 16px rgba(168, 85, 247, 0.4)' }}>{m.ipd?.netAssigned ?? 0}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>Active Cases</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#DC143C', textShadow: '0 0 16px rgba(220, 20, 60, 0.4)' }}>{m.ipd?.currentBacklog ?? 0}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>CSAT %</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900 }}>
                <span className={`glass-badge glass-badge--${csatStatusClass}`} style={{ fontSize: 'var(--font-size-lg)', padding: '4px 14px' }}>
                  {csatPct}%
                </span>
              </div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 'var(--space-1)', letterSpacing: '0.08em' }}>CSAT Avg</div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 900, color: '#FF6B8A', textShadow: '0 0 16px rgba(255, 107, 138, 0.4)' }}>{m.csat?.csatAvg ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTeamScore = () => {
    if (membersLoading) {
      return (
        <div className="flex-center" style={{ padding: 'var(--space-16)' }}><div className="spinner spinner--lg" /></div>
      );
    }

    if (teamError) {
      return (
        <div className="empty-state">
          <div className="empty-state__icon">⚠️</div>
          <div className="empty-state__title">Access Denied</div>
          <div className="empty-state__text">{teamError}</div>
          <div className="empty-state__text" style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>
            Please ask an Architect or Operations Manager to assign you a team.
          </div>
        </div>
      );
    }

    if (scoreMembers.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-state__icon">👥</div>
          <div className="empty-state__title">No Team Members</div>
          <div className="empty-state__text">No team members found with score data.</div>
        </div>
      );
    }

    // Determine animation class for the featured card container
    const featuredAnimClass = transitionPhase === 'shrinking'
      ? 'tl-featured--shrinking'
      : transitionPhase === 'expanding'
        ? 'tl-featured--expanding'
        : 'tl-featured--idle';

    return (
      <div>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          📊 Team Members — IPD + CSAT
        </h2>

        {/* Featured (big) card — animated container */}
        <div className={`tl-featured-card-container ${featuredAnimClass}`}>
          {selectedMember && renderMemberMetrics(selectedMember, true)}
        </div>

        {/* Small member cards grid */}
        {otherMembers.length > 0 && (
          <div className="tl-member-grid" style={{ marginTop: 'var(--space-4)' }}>
            {scoreMembers.map((m, i) => {
              if (m.userId === selectedMemberId) return null;
              // Apply special animation class to the incoming / outgoing cards
              const isIncoming = incomingId === m.userId;
              const isOutgoing = outgoingId === m.userId && transitionPhase === 'expanding';
              const cardAnimClass = isIncoming && transitionPhase === 'shrinking'
                ? 'tl-small--pulse'
                : isOutgoing
                  ? 'tl-small--appear'
                  : '';

              return (
                <div key={m.userId} className={`tl-member-grid-item ${cardAnimClass}`} style={{ animationDelay: `${i * 60}ms` }}>
                  {renderMemberMetrics(m, false)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderMonitorEscalations = () => {
    // NO data loading here — useEffect handles it

    const statusColors: Record<string, string> = {
      open: 'critical',
      see_discussion: 'in_progress',
      closed: 'success',
    };

    return (
      <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
        <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>👁 Team Escalations Monitor</h2>
          <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={loadEscalations}>Refresh</button>
        </div>

        {escLoading ? (
          <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
        ) : escalations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">✅</div>
            <div className="empty-state__title">No escalations</div>
            <div className="empty-state__text">Your team has no active escalations.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {escalations.map((esc) => (
              <div key={esc.id} className="glass-card glass-card--hover" style={{ padding: 'var(--space-4)', cursor: 'pointer' }}
                onClick={() => expandedEsc === esc.id ? setExpandedEsc(null) : loadEscThread(esc.id)}>
                <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
                  <div style={{ fontWeight: 700 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)', marginRight: 'var(--space-2)' }}>
                      {esc.caseNumber}
                    </span>
                    <span className={`glass-badge glass-badge--${statusColors[esc.status] || 'open'}`}>
                      {esc.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {new Date(esc.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                  {esc.description}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  Engineer: {esc.engineerName} • SME: {esc.smeName} • {esc.messageCount || 0} messages
                </div>

                {expandedEsc === esc.id && escThread && (
                  <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}
                    onClick={(e) => e.stopPropagation()}>
                    {escThread.messages?.map((msg: any) => (
                      <div key={msg.id} style={{
                        padding: 'var(--space-3)', marginBottom: 'var(--space-2)',
                        background: msg.senderRole === 'sme' ? 'rgba(77, 150, 255, 0.08)' : 'rgba(220, 20, 60, 0.06)',
                        borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${msg.senderRole === 'sme' ? '#4D96FF' : '#DC143C'}`,
                      }}>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
                          <strong>{msg.senderName}</strong> ({msg.senderRole}) • {new Date(msg.createdAt).toLocaleString()}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)' }}>{msg.message}</div>
                      </div>
                    ))}
                    {escThread.messages?.length === 0 && (
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
                        No messages yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'team-score': return renderTeamScore();
      case 'monitor-escalations': return renderMonitorEscalations();
      case 'dashboard': return renderDashboard();
      default: return renderDashboard();
    }
  };

  return (
    <AppShell role="team_leader" activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}

      {/* Score Modal */}
      {scoreModal && (
        <div className="glass-modal-overlay" onClick={() => setScoreModal(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">📊 {scoreModal.userName} — Score Details</span>
              <button className="glass-modal__close" onClick={() => setScoreModal(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="glass-card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>CSAT</h4>
                <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 800 }}>
                  {scoreModal.csat?.csatPercentage || 0}%
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                  Avg: {scoreModal.csat?.averageRating || 0} • {scoreModal.csat?.totalResponses || 0} surveys
                </div>
              </div>

              <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>
                Communicate Corrected Scores
              </h4>

              <div className="form-row" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Month</label>
                  <input className="glass-input" type="month" value={commForm.monthKey}
                    onChange={(e) => setCommForm({ ...commForm, monthKey: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Backlog</label>
                  <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={commForm.startingBacklog}
                    onChange={(e) => setCommForm({ ...commForm, startingBacklog: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
              </div>
              <div className="form-row" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Net Assigned</label>
                  <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={commForm.casesAssigned}
                    onChange={(e) => setCommForm({ ...commForm, casesAssigned: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Closure</label>
                  <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={commForm.casesClosed}
                    onChange={(e) => setCommForm({ ...commForm, casesClosed: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
              </div>

              <h4 style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 'var(--space-2)', marginTop: 'var(--space-3)' }}>CSAT Scores</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-2)' }}>
                {[5, 4, 3, 2, 1].map((n) => (
                  <div key={n} className="form-group">
                    <label className="form-label" style={{ textAlign: 'center' }}>★{n}</label>
                    <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" style={{ textAlign: 'center' }}
                      value={(commForm as any)[`n${n}`]}
                      onChange={(e) => setCommForm({ ...commForm, [`n${n}`]: e.target.value.replace(/[^0-9]/g, '') })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setScoreModal(null)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={sendCommunication}>Send to Member</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
